/**
 * rules.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

export enum Rule {
    ALLOW_NON_TRUSTED_LOCATION = 3,

    VIEW_SUBJECT = 5,
    SEARCH_SUBJECT,
    POPULATE_SUBJECT,
    LOAD_ALGORITHM,
    EXPLAIN_SUBJECT,

    CHAT = 15,

    VIEW_COMMENT = 20,
    COMMENT,

    SUBSCRIBE_SUBJECT = 27,
    SET_REMINDER,

    RUN_AGGREGATOR = 40,
    CREATE_AGGREGATOR,

    RUN_DATA_EXPORT = 45,
    CREATE_DATA_EXPORT,

    VIEW_SHARED_WORKLIST = 52,
    CREATE_SHARED_WORKLIST,

    REFER_SUBJECT = 61,

    USE_CLIENT_EXTENSIONS = 70,
    POSTPONE_SESSION_TIMEOUT,

    REVOKE_GROUP_VISIBILITY = 126,
    AUTHORIZE_GROUP_VISIBILITY = 127,
    SUPPORT_SYSTEM,

    DELETE_SUBJECT = 139,
    CREATE_GROUP,
    CREATE_ANY_GROUP,
    MANAGE_GROUP_COMMENTS,
    MANAGE_GROUP_REPORTS,
    // 144
    // 145
    ACCESS_USER_WORKLIST = 146,
    CREATE_GROUP_USER,
    RESET_USER_PASSWORD,
    RESET_NETWORK_USER_PASSWORD,
    VIEW_GROUP_USERS = 150,
    VIEW_USER,
    VIEW_NETWORK_USERS,
    VIEW_NETWORK_USER,
    UPDATE_USER,
    EDIT_USER_GROUP_RULES,
    EDIT_USER_GROUP_NETWORK_RULES,
    EDIT_USER_RULES,
    CREATE_USER,
    DEACTIVATE_USER,
    VIEW_GROUP_NETWORK = 160,
    VIEW_GROUP,
    EDIT_GROUP,
    EDIT_GROUP_RULES,
    EDIT_PRIMARY_GROUP_RULES,

    VIEW_ALL_ZONES = 170,
    EDIT_USER_ZONE_RULES,

    REQUEST_DELETE_SUBJECT = 180,

    VIEW_ALL_GROUP_NETWORKS = 200,
    VIEW_ALL_USERS,

    ADMINISTER_SYSTEM = 400,
    QUARANTINE_SYSTEM,
    QUARANTINE_ZONE,
    MANAGE_CLIENTS,
    NOTIFY_CLIENTS,

    SHUTDOWN_SYSTEM = 420,

    DEBUG_SYSTEM = 450,

    DEVELOP_ALGORITHM = 500,
    DEVELOP_SYSTEM,

    INITIATE_GUEST_SESSION = 600, // not really useful for the client!
    CREATE_TRANSIENT_SUBJECT
}

const ZONE_RELATIVE_RULES : Set<Rule> = new Set([
    /* ALLOW_NON_TRUSTED_LOCATION, VIEW_SUBJECT, SEARCH_SUBJECT, POPULATE_SUBJECT, LOAD_ALGORITHM, EXPLAIN_SUBJECT, CHAT, VIEW_COMMENT, COMMENT, SUBSCRIBE_SUBJECT, SET_REMINDER, RUN_AGGREGATOR, CREATE_AGGREGATOR, RUN_DATA_EXPORT, CREATE_DATA_EXPORT, REFER_SUBJECT, USE_CLIENT_EXTENSIONS, POSTPONE_SESSION_TIMEOUT, REVOKE_GROUP_VISIBILITY, AUTHORIZE_GROUP_VISIBILITY, SUPPORT_SYSTEM, DELETE_SUBJECT, CREATE_GROUP, CREATE_ANY_GROUP, MANAGE_GROUP_COMMENTS, MANAGE_GROUP_REPORTS, ACCESS_USER_WORKLIST, CREATE_GROUP_USER, RESET_USER_PASSWORD, RESET_NETWORK_USER_PASSWORD, VIEW_GROUP_USERS, VIEW_USER, VIEW_NETWORK_USER, VIEW_NETWORK_USERS, UPDATE_USER, EDIT_USER_GROUP_RULES, EDIT_USER_GROUP_NETWORK_RULES, EDIT_USER_RULES, CREATE_USER, DEACTIVATE_USER, VIEW_GROUP_NETWORK, VIEW_GROUP, EDIT_GROUP, EDIT_GROUP_RULES, EDIT_PRIMARY_GROUP_RULES, VIEW_ALL_ZONES, EDIT_USER_ZONE_RULES, REQUEST_DELETE_SUBJECT, VIEW_ALL_GROUP_NETWORKS, VIEW_ALL_USERS, ADMINISTER_SYSTEM, QUARANTINE_SYSTEM, QUARANTINE_ZONE, MANAGE_CLIENTS, NOTIFY_CLIENTS, SHUTDOWN_SYSTEM, DEBUG_SYSTEM, DEVELOP_ALGORITHM, DEVELOP_SYSTEM, INITIATE_GUEST_SESSION, CREATE_TRANSIENT_SUBJECT */
]);

const GROUP_RELATIVE_RULES : Set<Rule> = new Set([
    /* */
])

/**
 * A map of human-readable labels for each of the rules, when enumerating within
 * logs, dashboards, and user profiles.
 */
export const RULE_NAMES : Map<Rule, string> = new Map([
    [Rule.ALLOW_NON_TRUSTED_LOCATION, 
        "use the app from outside its trusted IP range"],
    [Rule.VIEW_SUBJECT, "view subjects"],
    [Rule.SEARCH_SUBJECT, "search subjects"],
    [Rule.POPULATE_SUBJECT, "populate subject data"],
    [Rule.LOAD_ALGORITHM, "load algorithms"],
    [Rule.EXPLAIN_SUBJECT, "request step explanations"],

    [Rule.CHAT, "chat with active users"],

    [Rule.VIEW_COMMENT, "view comments"],
    [Rule.COMMENT, "add comments"],

    [Rule.SUBSCRIBE_SUBJECT, "subscribe to subjects"],
    [Rule.SET_REMINDER, "set reminders"],

    [Rule.RUN_AGGREGATOR, "run aggregators"],
    [Rule.CREATE_AGGREGATOR, "create aggregator presets"],

    [Rule.RUN_DATA_EXPORT, "run data exports"],
    [Rule.CREATE_DATA_EXPORT, "create data export presets"],

    [Rule.REFER_SUBJECT, "refer subjects to external agencies"],

    [Rule.USE_CLIENT_EXTENSIONS, "enable collaboration plugins"],
    [Rule.POSTPONE_SESSION_TIMEOUT, "delay automated logout"],
    [Rule.REVOKE_GROUP_VISIBILITY, "reduce group-visibility of records"],
    [Rule.AUTHORIZE_GROUP_VISIBILITY, "expand group-visibility of records"],

    [Rule.SUPPORT_SYSTEM, "handle support requests"],

    [Rule.DELETE_SUBJECT, "delete subjects"],

    [Rule.CREATE_GROUP, "create child groups"],
    [Rule.CREATE_ANY_GROUP, "create root groups"],
    [Rule.MANAGE_GROUP_COMMENTS, "manage comments"],
    [Rule.MANAGE_GROUP_REPORTS, "manage reports owned by a group"],
    [Rule.ACCESS_USER_WORKLIST, "browse colleagues' worklists"],
    [Rule.CREATE_GROUP_USER, "create users within the group"],
    [Rule.RESET_USER_PASSWORD, "reset a group member's password"],
    [Rule.RESET_NETWORK_USER_PASSWORD, "reset a network member's password"],

    [Rule.VIEW_GROUP_USERS, "view a list of a group's users"],
    [Rule.VIEW_USER, "view a group member"],
    [Rule.VIEW_NETWORK_USER, "view a network member"],
    [Rule.VIEW_NETWORK_USERS, "view a list of users an entire network"],
    [Rule.UPDATE_USER, "make changes to a user"],
    [Rule.EDIT_USER_GROUP_RULES, "modify the rules of a user in a group"],
    [Rule.EDIT_USER_GROUP_NETWORK_RULES, "modify the rules of a use"],
    [Rule.EDIT_USER_RULES, "EDIT_USER_RULES"],
    [Rule.CREATE_USER, "CREATE_USER"],
    [Rule.DEACTIVATE_USER, "DEACTIVATE_USER"],
    [Rule.VIEW_GROUP_NETWORK, "VIEW_GROUP_NETWORK"],
    [Rule.VIEW_GROUP, "VIEW_GROUP"],
    [Rule.EDIT_GROUP, "EDIT_GROUP"],
    [Rule.EDIT_GROUP_RULES, "EDIT_GROUP_RULES"],
    [Rule.EDIT_PRIMARY_GROUP_RULES, "EDIT_PRIMARY_GROUP_RULES"],
    [Rule.VIEW_ALL_ZONES, "VIEW_ALL_ZONES"],
    [Rule.EDIT_USER_ZONE_RULES, "EDIT_USER_ZONE_RULES"],
    [Rule.REQUEST_DELETE_SUBJECT, "REQUEST_DELETE_SUBJECT"],
    [Rule.VIEW_ALL_GROUP_NETWORKS, "VIEW_ALL_GROUP_NETWORKS"],
    [Rule.VIEW_ALL_USERS, "VIEW_ALL_USERS"],

    [Rule.ADMINISTER_SYSTEM, "administer the system"],
    [Rule.QUARANTINE_SYSTEM, "quarantine the system"],
    [Rule.QUARANTINE_ZONE, "quarantine zones"],
    [Rule.MANAGE_CLIENTS, "view and terminate active connections"],
    [Rule.NOTIFY_CLIENTS, "broadcast system announcements"],

    [Rule.SHUTDOWN_SYSTEM, "prepare the system for shutdown"],

    [Rule.DEBUG_SYSTEM, "debug the system"],

    [Rule.DEVELOP_ALGORITHM, "develop algorithms"],
    [Rule.DEVELOP_SYSTEM, "develop the system"],

    [Rule.INITIATE_GUEST_SESSION, "INITIATE_GUEST_SESSION"],
    [Rule.CREATE_TRANSIENT_SUBJECT, "CREATE_TRANSIENT_SUBJECT"],
  ]);

type RuleTest = (rules : Rule[]) => boolean;

function and (args : Array<Rule | RuleTest>) : RuleTest
{
    const tests : RuleTest[] = args.map((arg : Rule | RuleTest) => (
            (typeof arg === "function")
                ? arg
                : ((rules : Rule[]) => rules.indexOf(arg) > -1)));
    return (rules : Rule[]) => tests.every((test : RuleTest) => test(rules));
}
function or (args : Array<Rule | RuleTest>) : RuleTest
{
    const tests : RuleTest[] = args.map((arg : Rule | RuleTest) => (
            (typeof arg === "function")
                ? arg
                : ((rules : Rule[]) => rules.indexOf(arg) > -1)));
    return (rules : Rule[]) => tests.some((test : RuleTest) => test(rules));
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/permissions/rules.ts